import type { AttackProfile, GearSlot, Item } from '../content/types.ts';
import type { Effect } from './effects.ts';

/**
 * Owning, wearing and carrying things.
 *
 * Audited from Rules for Survival pp.47-49 before anything was designed. What
 * the printed rules actually say, and therefore what this models:
 *
 *  - Inventory is WEIGHTLESS and there is no encumbrance (p.47). No weight is
 *    recorded anywhere.
 *  - **Only items in Gear Slots give bonuses. Items in the Inventory grant
 *    nothing** (p.48). That single sentence is why `equipped` is real,
 *    persisted, mechanical state rather than a display convenience.
 *  - Gear Slots are Head, Torso, Arms, Legs, Feet, Hands/Holding, Accessories.
 *    One item each, except Accessories which holds up to ten (p.48).
 *  - Damage Resistance comes from Armor, Magic and Buffs (p.42), so armour
 *    contributes an ordinary Modifier through the existing Effect pipeline.
 *    There is no second DR calculation anywhere.
 *
 * What is deliberately NOT here, because no supplied rule needs it: weight,
 * ammunition counts, durability and charges. See docs/RULES_CONFLICTS.md
 * OPEN-008. Do not add one because another game has it.
 */

/** RULE DCC-INV-003 - Rules p.48. The printed list, in the printed order. */
export const GEAR_SLOTS: readonly GearSlot[] = [
  'head',
  'torso',
  'arms',
  'legs',
  'feet',
  'hands',
  'accessories',
];

export const GEAR_SLOT_LABELS: Record<GearSlot, string> = {
  head: 'Head',
  torso: 'Torso',
  arms: 'Arms',
  legs: 'Legs',
  feet: 'Feet',
  hands: 'Hands/Holding',
  accessories: 'Accessories',
};

/**
 * What a Gear Slot holds.
 *
 * RULE DCC-INV-003 - "Each Gear Slot can contain at most one clothing item,
 * except for the Accessories slot, which can contain up to 10 items" (p.48).
 *
 * RULE DCC-INV-004 - Hands/Holding is TWO independent capacities, not one.
 * The page is explicit: "This one's a little different as you may BOTH wear
 * gloves (or similar) AND hold things in your hands. One item per hand, and one
 * pair of gloves." A single number of 2 would let a crawler wear gloves at the
 * cost of a hand, or hold three things if the gloves were off. Both are wrong.
 *
 * `held` is occupancy of the hands themselves; `worn` is gear worn on the body
 * part. Every other slot is worn-only.
 */
export interface SlotLimits {
  held: number;
  worn: number;
}

export function slotLimits(slot: GearSlot): SlotLimits {
  if (slot === 'hands') return { held: 2, worn: 1 };
  if (slot === 'accessories') return { held: 0, worn: 10 };
  return { held: 0, worn: 1 };
}

/**
 * Whether an Item in the Hands/Holding slot is held or worn.
 *
 * Only meaningful for that slot. An Item that does not say is HELD, because
 * everything the book describes going in a hand - weapons, shields - is held,
 * and gloves are the stated exception.
 */
export function handUseOf(item: Item): 'held' | 'worn' {
  return item.handUse ?? 'held';
}

/**
 * One kind of thing a crawler owns, and how many.
 *
 * Quantity, not one record per copy. The printed rules give no per-copy state
 * for any supplied item - no durability, no charges, no ammunition count - so
 * two Standard Mana Potions are genuinely interchangeable and modelling them as
 * distinct objects would invent state the rules do not have.
 *
 * `equipped` is the ONE piece of per-copy state, and it exists because the book
 * says only equipped items give bonuses. An entry with a quantity above one may
 * still have a single copy equipped, which is why this is a count and not a
 * flag: "I have three helmets and I am wearing one of them" is exactly the
 * example the book uses (p.48).
 */
export interface InventoryEntry {
  itemId: string;
  quantity: number;
  /** How many of them are in their Gear Slot. Never more than `quantity`. */
  equipped: number;
  /**
   * RULE DCC-INV-018 - how many are PHYSICALLY IN A HAND while NOT occupying
   * their own Gear Slot.
   *
   * `equipped` is MECHANICAL Gear Slot occupancy: a worn helmet, worn armour, a
   * wielded sword. Only those grant Effects (DCC-INV-002).
   *
   * `held` is PHYSICAL hand occupancy for an Item that is not in its own Gear
   * Slot: a spare helmet carried in a fist, a potion drawn from the Hotlist, a
   * rope with no Gear Slot at all. **It is not another Gear Slot.** It grants
   * nothing, and `itemEffects` never reads it.
   *
   * C&C p.15 is why it exists: "any item in your Hotlist can instantly appear
   * in a free hand." ANY item - `Item.slot` says where a thing is WORN, which
   * is a different question from whether a hand can hold it. A helmet can be
   * carried without being worn.
   *
   * ONE STATE, ONE REPRESENTATION. For an Item whose Gear Slot IS `hands` with
   * `handUse: 'held'`, the hand is its Gear Slot, so those copies stay in
   * `equipped` and `held` must be 0. Otherwise a sword could be written two
   * ways. `validateInventory` refuses the second one.
   */
  held: number;
}

export interface InventoryProblem {
  itemId: string;
  message: string;
}

/**
 * The Effects a crawler's inventory currently contributes.
 *
 * RULE DCC-INV-002 - equipped items ONLY. A stowed item contributes nothing,
 * which is expressed by simply not producing an Effect for it rather than by
 * producing an inactive one, so nothing downstream has to remember the rule.
 *
 * The result goes into `buildEffectiveCharacter` alongside Buffs and Debuffs,
 * so armour DR is resolved by the same code that resolves a Buff's DR.
 */
export function itemEffects(
  inventory: readonly InventoryEntry[],
  lookupItem: (id: string) => Item | undefined,
): Effect[] {
  const effects: Effect[] = [];
  for (const entry of inventory) {
    if (entry.equipped <= 0) continue;
    const item = lookupItem(entry.itemId);
    if (!item || item.modifiers.length === 0) continue;

    // One Effect per EQUIPPED copy. Wearing two of a thing that could legally
    // be worn twice - an Accessories slot holds ten - grants it twice, which is
    // what "only the items in these slots give bonuses" means.
    for (let copy = 0; copy < entry.equipped; copy += 1) {
      effects.push({
        id: entry.equipped === 1 ? `item:${item.id}` : `item:${item.id}#${copy + 1}`,
        sourceId: item.id,
        sourceKind: 'equipment',
        label: item.name,
        modifiers: item.modifiers,
      });
    }
  }
  return effects;
}

/** How much of each slot is in use, split by held and worn. */
/**
 * The two catalogues the Gear Slot rules need, together.
 *
 * Held occupancy depends on the Item AND on the Attack it represents (Rules
 * p.48: a two-handed weapon leaves no other hand free), so these functions
 * cannot work from Items alone. Bundled into one dependency rather than passed
 * as two unrelated callbacks: they are always needed together, and a caller
 * holding only half of them would be answering the wrong question.
 *
 * BOTH REQUIRED. There is deliberately no optional `lookupAttack` and no
 * fallback treating an unresolved weapon as one-handed - that fails OPEN,
 * quietly leaving a hand free for a warhammer wherever a caller forgot the
 * argument. The rules layer still knows nothing about where content comes from.
 */
export interface InventoryCatalogue {
  lookupItem: (id: string) => Item | undefined;
  lookupAttack: (id: string) => AttackProfile | undefined;
}

/**
 * How many HOLDING capacities one equipped copy of an Item takes up.
 *
 * RULE DCC-INV-016 (Rules p.48, Hands/Holding): "One item per hand... If a
 * weapon has 'Limitation: Two-Handed,' you cannot hold anything else." That is
 * expressed as occupancy against the EXISTING `held` capacity of 2 rather than
 * as a second hand-counting system, so `slotUsage`, `equipProblem` and
 * `validateInventoryAgainstCatalogue` all become correct at once. This is the
 * only place the number is worked out.
 *
 * Worn Hands items - gloves - are not held at all and are not this function's
 * business; callers ask only about the held capacity.
 */
export function heldSlotsFor(item: Item, catalogue: InventoryCatalogue): 1 | 2 {
  if (item.weaponAttackId === null) return 1;
  const attack = catalogue.lookupAttack(item.weaponAttackId);
  // A link that does not resolve, or resolves to something that is not a held
  // weapon, is a BROKEN Item rather than a one-handed one. `weaponLinkProblem`
  // refuses it outright and the catalogue validator reports it; the safe number
  // to return meanwhile is the larger one, so a broken link can never quietly
  // buy a free hand.
  if (!attack || attack.weaponUse === null) return 2;
  return attack.weaponUse.hands;
}

/**
 * What is wrong with a weapon relationship, as a KIND rather than a sentence.
 *
 * Two layers ask this question and neither can use the other's wording. The
 * catalogue validator produces fragments that read after "is equipped but...";
 * the custom Item editor produces imperatives telling the player what to change.
 * Sharing a message would make one of them read badly, and duplicating the
 * DECISION is what let the two drift apart in the first place.
 *
 * So the decision lives here once, and each layer words its own answer.
 */
export type WeaponLinkFault = 'unknown-attack' | 'not-a-weapon' | 'wrong-slot' | 'worn';

/**
 * Just enough of an Item to judge its weapon relationship.
 *
 * A draft is not an Item yet - it has no ID, no source, no name in the same
 * sense - so the shared check takes the fields it actually reads. That keeps
 * the primitive usable BEFORE an Item exists, which is the whole point.
 */
export interface WeaponLinkSubject {
  slot: GearSlot | null;
  handUse?: 'held' | 'worn';
  weaponAttackId: string | null;
}

/**
 * RULE DCC-INV-017, decided in ONE place.
 *
 * Returns what is wrong, or null. Deliberately takes `lookupAttack` alone
 * rather than a whole `InventoryCatalogue`: the question is about one Item and
 * the Attack it names, and a caller that has only an Attack catalogue - the
 * custom Item editor - can still ask it.
 *
 * NOTE the layer this does NOT serve. `validateCustomItemLibrary` checks the
 * stored file's SHAPE only, because it runs with no Attack catalogue in reach
 * and must not acquire a global registry dependency to get one. It checks that
 * the field is a string or null and that a weapon is a held Hands Item; whether
 * the named Attack exists and is a held weapon is decided at composition, where
 * a catalogue genuinely exists. That split is intentional and is documented in
 * RULES_MATRIX.md rather than papered over.
 */
export function weaponLinkFault(
  subject: WeaponLinkSubject,
  lookupAttack: (id: string) => AttackProfile | undefined,
): WeaponLinkFault | null {
  if (subject.weaponAttackId === null) return null;

  const attack = lookupAttack(subject.weaponAttackId);
  if (!attack) return 'unknown-attack';
  if (attack.weaponUse === null) return 'not-a-weapon';
  if (subject.slot !== 'hands') return 'wrong-slot';
  if ((subject.handUse ?? 'held') !== 'held') return 'worn';
  return null;
}

/**
 * Whether an Item's weapon relationship is coherent.
 *
 * RULE DCC-INV-017. Returns the reason it is not, or null. The DECISION comes
 * from `weaponLinkFault`, which the custom Item draft check also calls, so the
 * editor and the catalogue can never disagree about what is legal.
 *
 * The catalogue-aware layers are: this wrapper (used by
 * `validateInventoryAgainstCatalogue`, and through it the change pathway and
 * the import boundary), catalogue composition, and the draft check.
 * `validateCustomItemLibrary` is NOT one of them - it checks the stored file's
 * shape only, because it has no Attack catalogue in reach. Every stored Item
 * still passes composition before it can be equipped by anything.
 *
 * The whole contract, in one place so no caller re-derives a piece:
 *  - null is always fine, and is what a shield, torch, rope or glove has;
 *  - a non-null reference must resolve to a real Attack;
 *  - that Attack must have physical `weaponUse`, so a rope cannot claim to be
 *    Fireball, or Wrasslin', merely because both have IDs;
 *  - the Item must occupy the Hands slot, and be HELD rather than worn,
 *    because that is what a physical weapon is.
 */
export function weaponLinkProblem(item: Item, catalogue: InventoryCatalogue): string | null {
  const fault = weaponLinkFault(item, catalogue.lookupAttack);
  if (fault === null) return null;
  switch (fault) {
    case 'unknown-attack':
      return `names the Attack "${item.weaponAttackId}", which this installation does not know`;
    case 'not-a-weapon':
      return `names the Attack "${item.weaponAttackId}", which is not made with a held weapon`;
    case 'wrong-slot':
      return 'is a weapon, so it must occupy the Hands/Holding slot';
    case 'worn':
      return 'is a weapon, so it must be held rather than worn';
  }
}

export function slotUsage(
  inventory: readonly InventoryEntry[],
  catalogue: InventoryCatalogue,
): Record<GearSlot, SlotLimits> {
  const usage = {} as Record<GearSlot, SlotLimits>;
  for (const slot of GEAR_SLOTS) usage[slot] = { held: 0, worn: 0 };

  for (const entry of inventory) {
    const item = catalogue.lookupItem(entry.itemId);
    if (!item) continue;

    if (entry.equipped > 0 && item.slot !== null) {
      const use = item.slot === 'hands' ? handUseOf(item) : 'worn';
      // Held is the only capacity one copy can consume more than one of.
      const cost = use === 'held' ? heldSlotsFor(item, catalogue) : 1;
      usage[item.slot][use] += entry.equipped * cost;
    }

    // RULE DCC-INV-018 - a loose copy occupies a HAND, and it is the same hand
    // a sword would use. Added into the SAME `hands.held` number rather than
    // counted separately, so `equipProblem`, `holdProblem` and
    // `validateInventoryAgainstCatalogue` all become correct at once and there
    // is no second hand-capacity calculator to fall out of step. A spare pair
    // of gloves CARRIED costs a hand even though worn gloves do not.
    if (entry.held > 0) {
      usage.hands.held += entry.held * heldSlotsFor(item, catalogue);
    }
  }
  return usage;
}

/**
 * RULE DCC-INV-018 - where a copy taken into a hand actually goes.
 *
 * For an Item whose Gear Slot is `hands` with `handUse: 'held'`, holding it IS
 * equipping it, so the copy belongs in `equipped`. Everything else - a helmet,
 * a potion, a rope, a spare pair of gloves - belongs in `held`.
 *
 * ONE DECISION, ONE PLACE. Every pathway that puts something in a hand asks
 * this rather than re-deriving it, or the two would eventually disagree about a
 * shield.
 */
export function handPlacementFor(item: Item): 'gear-slot' | 'loose' {
  return item.slot === 'hands' && handUseOf(item) === 'held' ? 'gear-slot' : 'loose';
}

/**
 * Which persisted count a PHYSICALLY IN-HAND copy of this Item lives in.
 *
 * RULE DCC-INV-018, and the distinction that is easy to lose: `held` is the
 * name of a STORAGE FIELD, not the name of the physical state. A sword in a
 * fist is `equipped`, because a hand IS its Gear Slot; a carried helmet is
 * `held`. Both are in a hand.
 *
 * Reading `entry.held` to mean "in a hand" is therefore wrong for exactly the
 * Items whose hand is their Gear Slot, and it is reachable: a custom potion may
 * legitimately be authored `slot: 'hands'`, `handUse: 'held'`, at which point
 * readying it fills `equipped` and anything asking `entry.held` concludes the
 * crawler is holding nothing.
 */
export function handAllocationField(item: Item): 'equipped' | 'held' {
  return handPlacementFor(item) === 'gear-slot' ? 'equipped' : 'held';
}

/**
 * How many copies of this Item are PHYSICALLY IN A HAND, whichever count they
 * live in. THE one semantic answer - never `entry.held` on its own.
 */
export function inHandCopies(entry: InventoryEntry, item: Item | undefined): number {
  // Without the definition we cannot tell which field means "in hand", and
  // DCC-INV-017 already refuses to guess. A loose count is the safe reading:
  // an unresolved Item cannot legally be equipped anyway.
  if (item === undefined) return entry.held;
  return entry[handAllocationField(item)];
}

/**
 * Copies occupying no Gear Slot and no hand: `quantity - equipped - held`.
 *
 * NOT the complete answer to "how many are spare" - it cannot see the Hotlist,
 * so a copy backing a prepared slot is still counted here. Callers that need
 * the fully unallocated figure subtract `hotlistedCopies` themselves, which is
 * what `chooseUseRoute` and `physicalUseContext` do.
 */
export function unallocatedCopies(entry: InventoryEntry): number {
  return entry.quantity - entry.equipped - entry.held;
}

/**
 * Whether one more copy can be taken LOOSELY into a hand.
 *
 * Asks the SAME `slotUsage` capacity `equipProblem` trusts, so a held helmet
 * and a wielded sword compete for the same two hands. It differs from
 * `equipProblem` in what it does NOT require: the Item needs no Gear Slot at
 * all, because C&C p.15 lets any Hotlist Item appear in a hand.
 */
export function holdProblem(
  inventory: readonly InventoryEntry[],
  itemId: string,
  catalogue: InventoryCatalogue,
): string | null {
  const item = catalogue.lookupItem(itemId);
  if (!item) return `There is no Item with the ID "${itemId}".`;
  // A held-Hands Item is equipped, not held loosely. Routed rather than
  // refused, so no caller can create the second representation by accident.
  if (handPlacementFor(item) === 'gear-slot') return equipProblem(inventory, itemId, catalogue);

  const link = weaponLinkProblem(item, catalogue);
  if (link !== null) return `${item.name} ${link}.`;

  const entry = inventory.find((e) => e.itemId === itemId);
  if (!entry || entry.quantity <= 0) return `You do not have a ${item.name}.`;
  if (unallocatedCopies(entry) <= 0) {
    return `Every ${item.name} you own is already worn, held or prepared.`;
  }

  const limit = slotLimits('hands').held;
  const used = slotUsage(inventory, catalogue).hands.held;
  const needed = heldSlotsFor(item, catalogue);
  if (used + needed > limit) {
    return needed === 2
      ? `${item.name} needs both hands, and you have ${limit - used} free of ${limit}. ` +
          'Put something down first (Rules p.48).'
      : `Your hands are full: they hold ${limit} things (Rules p.48).`;
  }
  return null;
}

/**
 * Whether a crawler may equip one more of an item.
 *
 * Returns the reason it may not, or null when it may. Reads the CURRENT
 * inventory, so the caller does not have to work out what is already worn.
 */
export function equipProblem(
  inventory: readonly InventoryEntry[],
  itemId: string,
  catalogue: InventoryCatalogue,
): string | null {
  const item = catalogue.lookupItem(itemId);
  if (!item) return `There is no Item with the ID "${itemId}".`;
  if (item.petGear) return `${item.name} is pet equipment; equip it on the Companion tab.`;
  if (item.slot === null) return `${item.name} is not something you can wear or hold.`;
  // RULE DCC-INV-017 - a broken weapon link is refused as what it IS, before
  // occupancy is worked out. `heldSlotsFor` deliberately charges two hands for
  // an unresolvable link so nothing can quietly buy a free hand, but "your
  // hands are full" would be a confusing way to report a broken Item.
  const link = weaponLinkProblem(item, catalogue);
  if (link !== null) return `${item.name} ${link}.`;

  const entry = inventory.find((e) => e.itemId === itemId);
  if (!entry || entry.quantity <= 0) return `You do not have a ${item.name}.`;
  // RULE DCC-INV-018 - a copy already in a hand is not a spare one. Asking
  // only about `equipped` would let the same physical object be equipped and
  // carried at once.
  if (unallocatedCopies(entry) <= 0) {
    return `All of your ${item.name} are already equipped, held or prepared.`;
  }

  const use = item.slot === 'hands' ? handUseOf(item) : 'worn';
  const limit = slotLimits(item.slot)[use];
  const used = slotUsage(inventory, catalogue)[item.slot][use];
  // RULE DCC-INV-016 - ask whether there is room for THIS, not merely whether
  // the slot is already full. A two-handed weapon needs two free hands.
  const needed = use === 'held' ? heldSlotsFor(item, catalogue) : 1;
  if (used + needed > limit) {
    if (needed === 2) {
      const free = limit - used;
      return (
        `${item.name} needs both hands, and you have ${free} free of ${limit}. ` +
        'Put something down first (Rules p.48).'
      );
    }
    const what =
      item.slot === 'hands' && use === 'held'
        ? 'Your hands are full'
        : `Your ${GEAR_SLOT_LABELS[item.slot]} slot is full`;
    return `${what}: it holds ${limit} item${limit === 1 ? '' : 's'} (Rules p.48).`;
  }
  return null;
}

/**
 * Whether a crawler may set an owned Item's quantity to a given number.
 *
 * Returns the reason they may not, or null when they may. The same shape and
 * the same purpose as `equipProblem`: ONE implementation of the rule, called
 * both by the service that performs the change and by the interface that greys
 * the button out, so a control is never offered that is guaranteed to be
 * refused and the wording is never written twice.
 *
 * RULE DCC-INV-014 - quantity is OWNERSHIP. Lowering it below the equipped
 * count is refused rather than resolved by silently taking something off.
 *
 * RULE DCC-INV-012 - an Item this installation does not know is PRESERVED
 * while it is unequipped, so its count may still be adjusted: the crawler owns
 * the thing whether or not we know what it does. An unknown Item with copies
 * marked EQUIPPED is a different matter - that state grants nothing and cannot
 * legally be written back (see `validateInventoryAgainstCatalogue`), so its
 * count is left alone rather than half-changed.
 */
export function quantityProblem(
  inventory: readonly InventoryEntry[],
  itemId: string,
  quantity: number,
  lookupItem: (id: string) => Item | undefined,
): string | null {
  const entry = inventory.find((e) => e.itemId === itemId);
  if (!entry) return 'You do not have that Item.';

  const item = lookupItem(itemId);
  const name = item?.name ?? itemId;

  if (!Number.isInteger(quantity) || quantity < 1) {
    return `Keep at least one ${name}, or remove it entirely.`;
  }
  // RULE DCC-INV-014 + DCC-INV-018 - ownership cannot drop below the copies
  // that physically exist somewhere. Both allocations count: silently dropping
  // a carried copy loses a possession just as surely as dropping a worn one.
  const allocated = entry.equipped + entry.held;
  if (quantity < allocated) {
    const where =
      entry.held > 0 && entry.equipped > 0
        ? 'wearing or wielding and carrying'
        : entry.held > 0
          ? 'carrying'
          : 'wearing or holding';
    return (
      `You are ${where} ${allocated} ${name}. Put ${allocated - quantity} away first, then ` +
      'reduce the number.'
    );
  }
  if (!item && allocated > 0) {
    return (
      `${itemId} is not an Item this installation knows, and ${allocated} of them are ` +
      'marked as equipped or held. Its count cannot be changed here.'
    );
  }
  return null;
}

/**
 * Checks a whole inventory, for the record validator.
 *
 * The invariant that matters: `equipped` can never exceed `quantity`. That is
 * the shape a careless removal would break - taking the last copy away while
 * leaving it marked as worn - and it is exactly the sort of half-applied change
 * the inverse of a removal has to restore.
 */
export function validateInventory(inventory: readonly InventoryEntry[]): InventoryProblem[] {
  const problems: InventoryProblem[] = [];
  const seen = new Set<string>();

  for (const entry of inventory) {
    if (typeof entry.itemId !== 'string' || entry.itemId === '') {
      problems.push({ itemId: '', message: 'An inventory entry has no item ID.' });
      continue;
    }
    if (seen.has(entry.itemId)) {
      problems.push({ itemId: entry.itemId, message: 'appears more than once' });
    }
    seen.add(entry.itemId);

    if (!Number.isInteger(entry.quantity) || entry.quantity < 1) {
      problems.push({ itemId: entry.itemId, message: 'must have a whole quantity of at least 1' });
    }
    if (!Number.isInteger(entry.equipped) || entry.equipped < 0) {
      problems.push({ itemId: entry.itemId, message: 'has an impossible equipped count' });
    } else if (entry.equipped > entry.quantity) {
      problems.push({
        itemId: entry.itemId,
        message: `has ${entry.equipped} equipped but only ${entry.quantity} owned`,
      });
    }
    // RULE DCC-INV-018 - catalogue-free shape checks only. Whether a HELD copy
    // is legal for this particular Item needs its definition, so that question
    // belongs to `validateInventoryAgainstCatalogue`; the domain layer must not
    // reach for a registry to answer it.
    if (!Number.isInteger(entry.held) || entry.held < 0) {
      problems.push({ itemId: entry.itemId, message: 'has an impossible held count' });
    } else if (
      Number.isInteger(entry.equipped) &&
      entry.equipped >= 0 &&
      entry.equipped + entry.held > entry.quantity
    ) {
      problems.push({
        itemId: entry.itemId,
        message:
          `has ${entry.equipped} equipped and ${entry.held} held but only ${entry.quantity} owned`,
      });
    }
  }
  return problems;
}

/** The entry for an item, or undefined. */
export function entryFor(
  inventory: readonly InventoryEntry[],
  itemId: string,
): InventoryEntry | undefined {
  return inventory.find((e) => e.itemId === itemId);
}

export function quantityOf(inventory: readonly InventoryEntry[], itemId: string): number {
  return entryFor(inventory, itemId)?.quantity ?? 0;
}

export function equippedCount(inventory: readonly InventoryEntry[], itemId: string): number {
  return entryFor(inventory, itemId)?.equipped ?? 0;
}

/**
 * The CATALOGUE-AWARE inventory check.
 *
 * `validateInventory` above is catalogue-independent on purpose: the domain
 * validator must not reach for a global registry, and a crawler holding an ID
 * this installation does not have is a legitimate state (an import from a
 * machine with content we lack keeps its IDs rather than losing them).
 *
 * But Gear Slot capacity CANNOT be checked without knowing what the items are,
 * and it is a real invariant rather than a UI nicety: equipped items feed
 * Effects, so two Torso items worn at once would contribute both bonuses. UI
 * controls are not an integrity boundary - by the time a button is disabled,
 * an import or a `set-item` change has already had its chance.
 *
 * So this runs wherever a catalogue IS available: the shared CharacterChange
 * pathway when `lookupItem` is supplied, and the import boundary. Slice B's
 * equip and unequip service must call this rather than re-deriving capacity.
 */
export function validateInventoryAgainstCatalogue(
  inventory: readonly InventoryEntry[],
  catalogue: InventoryCatalogue,
): InventoryProblem[] {
  const problems = [...validateInventory(inventory)];

  for (const entry of inventory) {
    if (entry.equipped <= 0 && entry.held <= 0) continue;
    const item = catalogue.lookupItem(entry.itemId);
    if (!item) {
      problems.push({
        itemId: entry.itemId,
        message:
          entry.equipped > 0
            ? 'is equipped but is not an Item this installation knows'
            : // Held is refused for the same reason: without the definition we
              // cannot know how many hands it takes, and DCC-INV-017 already
              // refuses to guess. Unresolved Items are still PRESERVED while
              // both counts are zero.
              'is being held but is not an Item this installation knows',
      });
      continue;
    }
    // RULE DCC-INV-018, the one-representation invariant. A held-Hands Item in
    // a hand IS equipped; writing it as `held` too would give one physical
    // state two spellings and double its hand cost.
    if (entry.held > 0 && handPlacementFor(item) === 'gear-slot') {
      problems.push({
        itemId: entry.itemId,
        message:
          `${item.name} is held in a hand, which is its own Gear Slot, so it must be recorded ` +
          'as equipped rather than held',
      });
    }
    if (entry.equipped <= 0) continue;
    if (item.slot === null) {
      problems.push({
        itemId: entry.itemId,
        message: `is equipped but ${item.name} cannot be worn or held`,
      });
      continue;
    }
    // RULE DCC-INV-017 - an equipped Item whose weapon link is broken is an
    // integrity problem, never silently a one-handed weapon.
    const link = weaponLinkProblem(item, catalogue);
    if (link !== null) {
      problems.push({ itemId: entry.itemId, message: `is equipped but ${link}` });
    }
  }

  // Capacity is per SLOT, across every item in it - the failure this exists to
  // catch is two DIFFERENT Torso items, which no per-entry check would see.
  const usage = slotUsage(inventory, catalogue);
  for (const slot of GEAR_SLOTS) {
    const limits = slotLimits(slot);
    for (const use of ['held', 'worn'] as const) {
      if (usage[slot][use] <= limits[use]) continue;
      problems.push({
        itemId: slot,
        message:
          `${GEAR_SLOT_LABELS[slot]} has ${usage[slot][use]} ${use} ` +
          `but holds at most ${limits[use]} (Rules p.48)`,
      });
    }
  }

  return problems;
}
